import { useRef, useState, type CSSProperties } from "react";
import type { Constellation, PhotoFrame, Sighting, View } from "@/api/types";
import { formatDate, formatLicense, sourceLabel, viewLabel } from "@/lib/format";
import { Plate, sharkAspect } from "@/site/plate";

/** Frame shape per view, so every photo of a view is cropped alike: flanks lie long, a
 * top-down shark stands head-up. A photo of unclear view gets its own shape. */
const VIEW_ASPECT: Record<View, number> = { left: 2, right: 2, top: 0.75 };
const VIEW_ORDER: readonly (View | null)[] = ["left", "right", "top", null];
/** Plates this far either side of the one in view load their photo; the rest wait. */
const LOAD_AHEAD = 3;

export interface ReelPhoto {
  sighting: Sighting;
  url: string;
  /** The photo's outline as a spotless constellation, which the plate turns to its view. */
  outline: Constellation;
  aspect: number;
}

function outlineOf(frame: PhotoFrame): Constellation {
  return { ...frame, spots: [], fin: null };
}

/** The photographed sightings, grouped by view (left, right, top, unclear), each group in
 * the order given. Spot-only sightings have no photo to show and are left out. */
export function reelPhotos(sightings: readonly Sighting[]): ReelPhoto[] {
  const photos: ReelPhoto[] = [];
  for (const view of VIEW_ORDER) {
    for (const s of sightings) {
      if (s.view !== view || !s.thumb_url || !s.frame) continue;
      const outline = outlineOf(s.frame);
      const aspect = view ? VIEW_ASPECT[view] : sharkAspect(outline, 1, 2.4);
      photos.push({ sighting: s, url: s.thumb_url, outline, aspect });
    }
  }
  return photos;
}

function scrollBehavior(): ScrollBehavior {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
}

function Credit({ s }: { s: Sighting }) {
  const where = [s.locality, s.country].filter(Boolean).join(", ");
  const source = s.source_url ? (
    <a href={s.source_url} className="link-u" target="_blank" rel="noreferrer">
      {sourceLabel(s.source)}
    </a>
  ) : (
    sourceLabel(s.source)
  );
  return (
    <figcaption className="fig-cap label reel-cap">
      <span>
        {[viewLabel(s.view) ?? "View unclear", formatDate(s.date), where]
          .filter(Boolean)
          .join(" · ")}
      </span>
      <span>
        {s.photographer ? `${s.photographer} · ` : ""}
        {source}
        {formatLicense(s.license) ? ` · ${formatLicense(s.license)}` : ""}
      </span>
    </figcaption>
  );
}

/**
 * Every photograph of a shark in one scrolling strip, each turned to face its view's way and
 * cropped to the shark's outline, so a view's photos line up like plates in an atlas. Turned,
 * never mirrored: a mirrored flank would show a pattern the shark lacks.
 */
export function PhotoReel({ sightings, name }: { sightings: readonly Sighting[]; name: string }) {
  const photos = reelPhotos(sightings);
  const strip = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(0);
  const photo = photos[current];
  if (!photo) return <p className="note">No photograph on file.</p>;

  const onScroll = () => {
    const el = strip.current;
    if (!el) return;
    let nearest = 0;
    for (let i = 0; i < el.children.length; i++) {
      const child = el.children[i] as HTMLElement;
      const best = el.children[nearest] as HTMLElement;
      if (Math.abs(child.offsetLeft - el.scrollLeft) < Math.abs(best.offsetLeft - el.scrollLeft)) {
        nearest = i;
      }
    }
    setCurrent(nearest);
  };
  const go = (step: number) => {
    const el = strip.current;
    const target = el?.children[current + step] as HTMLElement | undefined;
    if (el && target) el.scrollTo({ left: target.offsetLeft, behavior: scrollBehavior() });
  };

  return (
    <>
      <div className="fig-head">
        <span className="label">Fig. A · Catalogue photographs, turned to their view</span>
        <span className="label reel-nav">
          <button
            type="button"
            onClick={() => go(-1)}
            disabled={current === 0}
            aria-label="Previous photograph"
          >
            ←
          </button>
          <span aria-live="polite">
            {current + 1} / {photos.length}
          </span>
          <button
            type="button"
            onClick={() => go(1)}
            disabled={current === photos.length - 1}
            aria-label="Next photograph"
          >
            →
          </button>
        </span>
      </div>
      <div
        ref={strip}
        className="reel"
        onScroll={onScroll}
        role="region"
        aria-label={`Photographs of ${name}; scroll sideways for more`}
      >
        {photos.map((p, i) => (
          <div
            key={p.sighting.image_key}
            className="reel-slide"
            style={{ "--aspect": p.aspect } as CSSProperties}
            aria-current={i === current ? "true" : undefined}
          >
            {Math.abs(i - current) <= LOAD_AHEAD ? (
              <Plate
                imageUrl={p.url}
                constellation={p.outline}
                aspect={p.aspect}
                label={`${name}, ${viewLabel(p.sighting.view)?.toLowerCase() ?? "view unclear"}, ${formatDate(p.sighting.date) ?? "undated"}`}
              />
            ) : (
              <div className="plate" style={{ aspectRatio: p.aspect }} />
            )}
          </div>
        ))}
      </div>
      <Credit s={photo.sighting} />
    </>
  );
}
